<template>
  <transition name="modal-fade">
    <div class="shared-modal-overlay" v-if="show && item" @click="handleOverlayClick">
      <div class="shared-modal-container" @click.stop>
        <!-- Botón de cerrar -->
        <button @click="closeModal" class="modal-close-btn" aria-label="Cerrar">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        <!-- Etiqueta de "Compartido" -->
        <div class="shared-badge">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="18" cy="5" r="3"/>
            <circle cx="6" cy="12" r="3"/>
            <circle cx="18" cy="19" r="3"/>
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
          </svg>
          <span>Contenido compartido</span>
        </div>

        <!-- Card de noticia - Usa el componente correcto según el tipo -->
        <div class="shared-modal-card">
          <!-- Usar la misma lógica que FeedList para elegir el componente -->
          <component
            :is="getCardComponent"
            :item="item"
            :is-modal="true"
            @play-video="handlePlayVideo"
            @play-short="handlePlayShort"
          />
        </div>

        <!-- Footer con acciones adicionales -->
        <div class="shared-modal-footer">
          <button @click="copyCurrentUrl" class="modal-action-btn">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2 2v1"/>
            </svg>
            <span>{{ copyButtonText }}</span>
          </button>
        </div>
      </div>

      <!-- YouTube Player Modal -->
      <YouTubePlayer ref="youtubePlayer" />

      <!-- Shorts Player Modal -->
      <ShortsVideoPlayer ref="shortsPlayer" />
    </div>
  </transition>
</template>

<script>
import NewsCard from './NewsCard.vue';
import NewsWithImageCard from './NewsWithImageCard.vue';
import VideoCard from './VideoCard.vue';
import YouTubePlayer from './YouTubePlayer.vue';
import ShortsVideoPlayer from './ShortsVideoPlayer.vue';

export default {
  name: 'SharedModal',
  components: {
    NewsCard,
    NewsWithImageCard,
    VideoCard,
    YouTubePlayer,
    ShortsVideoPlayer
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    item: {
      type: Object,
      required: false,
      default: null
    }
  },
  data() {
    return {
      copyButtonText: 'Copiar enlace'
    };
  },
  computed: {
    // Usar la misma lógica que FeedList para elegir el componente correcto
    getCardComponent() {
      if (!this.item) return 'NewsCard';

      // Si es YouTube o TikTok, mostrar VideoCard
      if (this.item.sourceType === 'youtube' || this.item.sourceType === 'tiktok') {
        return 'VideoCard';
      }
      // Si tiene imagen (y no es video), mostrar NewsWithImageCard
      if (this.item.image) {
        return 'NewsWithImageCard';
      }
      // Por defecto, mostrar NewsCard
      return 'NewsCard';
    }
  },
  methods: {
    closeModal() {
      this.$emit('close');
    },
    handleOverlayClick() {
      // Cerrar al hacer click en el overlay
      this.closeModal();
    },
    handlePlayVideo(videoId, title) {
      // Abrir el reproductor de YouTube (videos normales)
      this.$refs.youtubePlayer.openPlayer(videoId, title);
    },
    handlePlayShort(videoId, title, videoType = 'youtube') {
      // Abrir el reproductor de Shorts (YouTube Shorts o TikTok)
      this.$refs.shortsPlayer.openPlayer(videoId, title, videoType);
    },
    async copyCurrentUrl() {
      try {
        const currentUrl = window.location.href;

        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(currentUrl);
        } else {
          // Fallback
          const textarea = document.createElement('textarea');
          textarea.value = currentUrl;
          textarea.style.position = 'fixed';
          textarea.style.opacity = '0';
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
        }

        // Feedback visual
        this.copyButtonText = '¡Copiado!';
        setTimeout(() => {
          this.copyButtonText = 'Copiar enlace';
        }, 2000);
      } catch (error) {
        console.error('Error copying URL:', error);
        this.copyButtonText = 'Error al copiar';
        setTimeout(() => {
          this.copyButtonText = 'Copiar enlace';
        }, 2000);
      }
    }
  },
  mounted() {
    // Prevenir scroll del body cuando el modal está abierto
    if (this.show) {
      document.body.style.overflow = 'hidden';
    }
  },
  beforeDestroy() {
    // Restaurar scroll del body
    document.body.style.overflow = '';
  },
  watch: {
    show(newVal) {
      if (newVal) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    }
  }
};
</script>

<style scoped>
/* Overlay del modal */
.shared-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  overflow-y: auto;
}

/* Container del modal */
.shared-modal-container {
  position: relative;
  max-width: 700px;
  width: 100%;
  /* margin auto centra, pero si la card es más alta que la pantalla queda arriba sin cortarse */
  margin: auto;
  background: transparent;
  animation: modal-slide-up 0.3s ease-out;
}

/* Botón de cerrar */
.modal-close-btn {
  position: absolute;
  top: -50px;
  right: 0;
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;
}

.modal-close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: rotate(90deg);
}

/* Badge de compartido */
.shared-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: linear-gradient(135deg, #4a9eff, #7b68ee);
  border-radius: 12px;
  margin-bottom: 16px;
  color: white;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(74, 158, 255, 0.3);
}

.shared-badge svg {
  flex-shrink: 0;
}

/* Card del modal */
.shared-modal-card {
  width: 100%;
  animation: card-appear 0.4s ease-out 0.1s both;
}

/* Hacer el card un poco más grande en el modal */
.shared-modal-card >>> .news-card,
.shared-modal-card >>> .video-card {
  transform: scale(1);
  transform-origin: top center;
}

.shared-modal-card >>> .news-card:hover,
.shared-modal-card >>> .video-card:hover {
  transform: scale(1) /*translateY(-4px); */
}

/* Footer con acciones */
.shared-modal-footer {
  margin-top: 16px;
  display: flex;
  justify-content: center;
  gap: 12px;
}

.modal-action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
}

.modal-action-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

.modal-action-btn svg {
  flex-shrink: 0;
}

/* Animaciones */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

@keyframes modal-slide-up {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes card-appear {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .shared-modal-overlay {
    padding: 16px;
    padding-top: 60px;
  }

  .shared-modal-container {
    max-width: 100%;
  }

  .modal-close-btn {
    top: -50px;
    right: 0;
    width: 40px;
    height: 40px;
  }

  .shared-badge {
    font-size: 13px;
    padding: 10px 16px;
  }

  .modal-action-btn {
    padding: 10px 20px;
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .shared-modal-overlay {
    padding: 12px;
    padding-top: 50px;
  }

  .modal-close-btn {
    top: -45px;
    width: 36px;
    height: 36px;
  }

  .modal-close-btn svg {
    width: 20px;
    height: 20px;
  }

  .shared-badge {
    font-size: 12px;
    padding: 8px 12px;
  }

  .shared-badge svg {
    width: 14px;
    height: 14px;
  }

  .modal-action-btn {
    padding: 8px 16px;
    font-size: 12px;
    flex: 1;
  }

  .modal-action-btn svg {
    width: 16px;
    height: 16px;
  }
}

/* Fullscreen en mobile muy pequeño */
@media (max-width: 400px) {
  .shared-modal-overlay {
    padding: 8px;
    padding-top: 45px;
  }
}
</style>
